import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const STATUS_STYLES = {
  paid: "border-[#E5C56F]/40 bg-[#E5C56F]/10 text-[#E5C56F]",
  pending: "border-white/15 bg-white/[0.03] text-zinc-400",
  failed: "border-red-500/30 bg-red-500/10 text-red-300",
  expired: "border-red-500/30 bg-red-500/10 text-red-300",
  refunded: "border-purple-500/30 bg-purple-600/10 text-purple-300",
};

const PaymentsPanel = ({ adminKey }) => {
  const [payments, setPayments] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/crm/payments`)
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => setErr("Failed to load payments."));
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm" data-testid="payments-error">{err}</div>;
  if (!payments)
    return (
      <div className="flex items-center gap-3 text-zinc-400" data-testid="payments-loading">
        <Loader2 className="animate-spin" size={16} /> Loading payments…
      </div>
    );

  const paidCount = payments.filter((p) => p.payment_status === "paid").length;
  const paidTotal = payments.filter((p) => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  if (payments.length === 0)
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-zinc-500" data-testid="payments-empty">
        No payments yet. When someone pays for a Strategy Call, it appears here.
      </div>
    );

  return (
    <div className="space-y-4" data-testid="payments-panel">
      <div className="flex flex-wrap items-center gap-4">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
          {payments.length} checkout{payments.length === 1 ? "" : "s"}
        </div>
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#E5C56F]" data-testid="payments-paid-total">
          {paidCount} paid · ${(paidTotal / 100).toFixed(0)} collected
        </div>
      </div>
      {payments.map((p) => {
        const st = p.payment_status || "pending";
        return (
          <div key={p.session_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-3" data-testid={`payment-${p.session_id}`}>
            <div className="w-9 h-9 rounded-lg border border-[#E5C56F]/30 bg-[#E5C56F]/10 flex items-center justify-center text-[#E5C56F] shrink-0">
              <CreditCard size={16} strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="text-sm text-zinc-100 font-medium truncate" data-testid="payment-email">
                {p.customer_email || "No email yet"}
              </div>
              <div className="text-[11px] text-zinc-500">
                Strategy Call · ${((p.amount || 0) / 100).toFixed(0)} {(p.currency || "usd").toUpperCase()} · {fmt(p.created_at)}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[st] || STATUS_STYLES.pending}`}
                data-testid="payment-status"
              >
                {st}
              </span>
              {p.customer_email && (
                <a
                  href={`mailto:${p.customer_email}?subject=Your%20Ah-Jin%20Strategy%20Call`}
                  data-testid="payment-mailto"
                  className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/40 bg-purple-600/10 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-purple-200 hover:bg-purple-600/20 transition-colors"
                >
                  <Mail size={11} /> Email
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PaymentsPanel;
